import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Chip, Stack, IconButton, Tooltip, Checkbox, FormControlLabel, FormGroup,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import WebhookIcon from '@mui/icons-material/Webhook';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Swal from 'sweetalert2';
import request from '../../../services/Request';
import ModernTable, { ModernPageHeader } from '../components/ModernTable';

const BASE = '/external/admin';

const ALL_EVENTS = [
  { value: 'member.registered', label: 'Member Registered' },
  { value: 'member.purchase', label: 'Member Purchase' },
  { value: 'order.completed', label: 'Order Completed' },
];

export default function Webhooks() {
  const theme = useTheme();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState(['member.registered', 'member.purchase', 'order.completed']);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    request({ method: 'get', url: `${BASE}/webhooks` }).then((res) => {
      if (!cancelled) { setWebhooks(res.data); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [refresh]);

  const toggleEvent = (value) =>
    setEvents((prev) => prev.includes(value) ? prev.filter((e) => e !== value) : [...prev, value]);

  const handleCreate = async () => {
    if (!url.trim() || events.length === 0) return;
    try {
      await request({ method: 'post', url: `${BASE}/webhooks`, data: { url, events } });
      Swal.fire({ icon: 'success', text: 'Webhook registered', timer: 1500, showConfirmButton: false });
      setOpen(false);
      setUrl('');
      setEvents(['member.registered', 'member.purchase', 'order.completed']);
      refresh ? setRefresh(false) : setRefresh(true);
    } catch {
      Swal.fire({ icon: 'error', text: 'Failed to register webhook' });
    }
  };

  const handleDelete = async (id, webhookUrl) => {
    const confirm = await Swal.fire({
      title: 'Delete webhook?', text: webhookUrl, icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Delete',
      confirmButtonColor: theme.palette.error.main,
    });
    if (!confirm.isConfirmed) return;
    await request({ method: 'delete', url: `${BASE}/webhooks/${id}` });
    Swal.fire({ icon: 'success', text: 'Webhook deleted', timer: 1200, showConfirmButton: false });
    refresh ? setRefresh(false) : setRefresh(true);
  };

  const columns = [
    {
      title: 'URL', field: 'url',
      render: (row) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', color: theme.palette.primary.main }}>
          {row.url}
        </Typography>
      ),
    },
    {
      title: 'Events', field: 'events',
      render: (row) => (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {(row.events || []).map((e) => (
            <Chip key={e} label={e} size="small" sx={{ fontSize: 10, height: 20, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }} />
          ))}
        </Stack>
      ),
    },
    {
      title: 'Status', field: 'active',
      render: (row) => (
        <Chip
          label={row.active ? 'Active' : 'Inactive'} size="small"
          sx={{ bgcolor: row.active ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                color: row.active ? theme.palette.success.dark : theme.palette.error.dark }}
        />
      ),
    },
    {
      title: 'Created', field: 'createdAt',
      render: (row) => <Typography variant="body2" color="text.secondary">{new Date(row.createdAt).toLocaleDateString()}</Typography>,
    },
    {
      title: 'Action', field: '_id',
      render: (row) => (
        <Tooltip title="Delete webhook">
          <IconButton size="small" onClick={() => handleDelete(row._id, row.url)}
            sx={{ color: theme.palette.error.main, '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) } }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <ModernPageHeader
        title="Webhooks"
        subtitle="Push real-time events to external systems when members register or purchase"
        icon={WebhookIcon}
        buttonText="Register Webhook"
        buttonIcon={AddIcon}
        onButtonClick={() => setOpen(true)}
      />

      <ModernTable
        isLoading={loading}
        columns={columns}
        data={webhooks}
        options={{ actionsColumnIndex: -1, pageSize: 10, search: false }}
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Register Webhook</DialogTitle>
        <DialogContent>
          <TextField
            label="URL" value={url} onChange={(e) => setUrl(e.target.value)}
            fullWidth variant="outlined" placeholder="https://your-server.com/webhook"
            sx={{ mt: 1, mb: 2 }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Events</Typography>
          <FormGroup>
            {ALL_EVENTS.map((ev) => (
              <FormControlLabel
                key={ev.value}
                control={<Checkbox checked={events.includes(ev.value)} onChange={() => toggleEvent(ev.value)} size="small" />}
                label={<Typography variant="body2">{ev.label}</Typography>}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={!url.trim() || events.length === 0}>
            Register
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
